import { useEffect, useRef } from "react";

// Una lectora 2D USB se comporta como un teclado: "tipea" el contenido del QR de corrido y cierra
// con Enter. Se la distingue de una persona por la velocidad: entre tecla y tecla pasan pocos ms.
const MAX_GAP_MS = 100;
// Sin un largo mínimo, un Enter suelto o un par de teclas rápidas dispararían un escaneo.
const MIN_CODE_LENGTH = 6;

type UseHidQrScannerArgs = {
  enabled: boolean;
  onScan: (qr: string) => void;
};

/**
 * Escucha a la lectora de QR (modo teclado) y avisa con el contenido leído.
 *
 * No usa `useScan`: ese hook resuelve QR de usuario y PDF417 de DNI, y acá el contenido va crudo a
 * `scanSpace`, que es quien lo valida. Requiere que la lectora esté configurada con Enter como
 * sufijo, igual que para el resto de la app.
 */
const useHidQrScanner = ({ enabled, onScan }: UseHidQrScannerArgs) => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;

    let buffer = "";
    let lastKeyAt = 0;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;

      const now = Date.now();
      if (now - lastKeyAt > MAX_GAP_MS) buffer = "";
      lastKeyAt = now;

      const isTerminator = event.key === "Enter" || event.key === "Tab";
      if (isTerminator) {
        const code = buffer;
        buffer = "";
        if (code.length >= MIN_CODE_LENGTH) {
          // El Enter de la lectora no debe activar el botón que tenga el foco (p. ej. "Escanear QR").
          event.preventDefault();
          event.stopImmediatePropagation();
          onScanRef.current(code);
        }
        return;
      }

      // Sólo caracteres imprimibles: `key` ya viene resuelto por el layout activo. Los atajos
      // (Ctrl/Cmd) se ignoran; AltGr llega como Ctrl+Alt y sí vale.
      if (event.key.length !== 1) return;
      if ((event.ctrlKey || event.metaKey) && !event.altKey) return;
      buffer += event.key;
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [enabled]);
};

export default useHidQrScanner;
